import { Component, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { LayoutService } from '../../core/services/layout.service';

interface NavItem {
  label: string;
  route: string;
  icon:  string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <nav class="sidebar" [class.open]="layout.isSidebarOpen()">
      <!-- Logo -->
      <div class="sidebar-logo">
        <div class="sidebar-logo-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <rect x="3" y="3" width="7" height="7" rx="1"></rect>
            <rect x="14" y="3" width="7" height="7" rx="1"></rect>
            <rect x="3" y="14" width="7" height="7" rx="1"></rect>
            <rect x="14" y="14" width="7" height="7" rx="1"></rect>
          </svg>
        </div>
        <span class="sidebar-logo-text">Gridxum</span>
        <span class="sidebar-badge">Admin</span>
      </div>

      <!-- Navigation -->
      <div class="sidebar-nav">
        <span class="sidebar-section-label">Panel</span>
        <a
          *ngFor="let item of navItems"
          [routerLink]="item.route"
          routerLinkActive="active"
          class="sidebar-link"
          [attr.aria-label]="item.label"
          (click)="layout.closeSidebar()"
        >
          <span class="sidebar-link-icon" [innerHTML]="item.icon"></span>
          <span class="sidebar-link-label">{{ item.label }}</span>
        </a>
      </div>

      <!-- Footer -->
      <div class="sidebar-footer">
        <!-- User info -->
        <div class="sidebar-user" *ngIf="auth.currentUser() as user">
          <div class="sidebar-user-avatar">
            <img *ngIf="user.image" [src]="user.image" [alt]="user.name" />
            <span *ngIf="!user.image">{{ getInitials(user.name) }}</span>
          </div>
          <div class="sidebar-user-info">
            <span class="sidebar-user-name">{{ user.name }}</span>
            <span class="sidebar-user-email">{{ user.email }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="sidebar-actions">
          <!-- Dark/Light toggle -->
          <button
            class="sidebar-action-btn"
            (click)="theme.toggle()"
            [attr.aria-label]="theme.isDarkMode() ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
            id="theme-toggle"
          >
            <svg *ngIf="theme.isDarkMode()" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
            <svg *ngIf="!theme.isDarkMode()" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          </button>

          <!-- Logout -->
          <button
            class="sidebar-action-btn logout"
            (click)="auth.logout()"
            aria-label="Cerrar sesión"
            id="logout-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>
    </nav>
  `,
  styles: [`
    .sidebar {
      width: var(--sidebar-width);
      min-width: var(--sidebar-width);
      height: 100vh;
      position: sticky;
      top: 0;
      background: var(--bg-sidebar);
      border-right: 1px solid hsla(0, 0%, 100%, 0.06);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: transform var(--transition-normal);
      z-index: 50;
    }

    @media (max-width: 768px) {
      .sidebar {
        position: fixed;
        left: 0;
        transform: translateX(-100%);
      }
      .sidebar.open {
        transform: translateX(0);
        box-shadow: var(--shadow-xl);
      }
    }

    /* Logo */
    .sidebar-logo {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-6) var(--space-5);
      border-bottom: 1px solid hsla(0, 0%, 100%, 0.06);
    }
    .sidebar-logo-icon {
      width: 36px;
      height: 36px;
      border-radius: var(--radius-md);
      background: var(--color-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
    }
    .sidebar-logo-text {
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-bold);
      color: #fff;
      flex: 1;
    }
    .sidebar-badge {
      font-size: 10px;
      font-weight: var(--font-weight-semibold);
      background: hsla(234, 70%, 60%, 0.25);
      color: hsl(234, 85%, 75%);
      padding: 2px 8px;
      border-radius: var(--radius-full);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    /* Nav */
    .sidebar-nav {
      flex: 1;
      padding: var(--space-4) var(--space-3);
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
    }
    .sidebar-section-label {
      font-size: 10px;
      font-weight: var(--font-weight-semibold);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--sidebar-text);
      padding: var(--space-2) var(--space-3);
      opacity: 0.6;
    }
    .sidebar-link {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-3) var(--space-3);
      border-radius: var(--radius-md);
      color: var(--sidebar-text);
      transition: all var(--transition-fast);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
    }
    .sidebar-link:hover {
      background: var(--sidebar-hover-bg);
      color: #fff;
    }
    .sidebar-link.active {
      background: var(--sidebar-active-bg);
      color: var(--sidebar-active-text);
    }
    .sidebar-link-icon {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      opacity: 0.75;
    }
    .sidebar-link.active .sidebar-link-icon,
    .sidebar-link:hover .sidebar-link-icon {
      opacity: 1;
    }

    /* Footer */
    .sidebar-footer {
      padding: var(--space-4) var(--space-3);
      border-top: 1px solid hsla(0, 0%, 100%, 0.06);
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
    }
    .sidebar-user {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-3);
      border-radius: var(--radius-md);
      background: hsla(0, 0%, 100%, 0.04);
    }
    .sidebar-user-avatar {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: var(--color-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-bold);
      overflow: hidden;
      flex-shrink: 0;
    }
    .sidebar-user-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .sidebar-user-info { flex: 1; min-width: 0; }
    .sidebar-user-name {
      display: block;
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-semibold);
      color: #fff;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .sidebar-user-email {
      display: block;
      font-size: 10px;
      color: var(--sidebar-text);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 1px;
    }

    .sidebar-actions {
      display: flex;
      gap: var(--space-2);
    }
    .sidebar-action-btn {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 36px;
      border-radius: var(--radius-md);
      color: var(--sidebar-text);
      background: hsla(0, 0%, 100%, 0.04);
      transition: all var(--transition-fast);
    }
    .sidebar-action-btn:hover {
      background: hsla(0, 0%, 100%, 0.1);
      color: #fff;
    }
    .sidebar-action-btn.logout:hover {
      background: hsla(0, 72%, 51%, 0.2);
      color: hsl(0, 72%, 70%);
    }
  `]
})
export class SidebarComponent {
  readonly auth   = inject(AuthService);
  readonly theme  = inject(ThemeService);
  readonly layout = inject(LayoutService);

  readonly navItems: NavItem[] = [
    {
      label: 'Usuarios',
      route: '/usuarios',
      icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>`
    },
    {
      label: 'CVs',
      route: '/cvs',
      icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>`
    },
    {
      label: 'Soporte',
      route: '/soporte',
      icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>`
    },
    {
      label: 'Métricas',
      route: '/metricas',
      icon: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>`
    }
  ];

  getInitials(name: string): string {
    return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
  }
}
